/**
 * The format, and the compiler that turns it into notes.
 *
 * A piece describes constraints. It does not contain a melody, and that is the
 * point: the notes are chosen at render time from pools the key and chord make
 * available, so a randomised piece is still in key, still on the beat, and
 * still lands on chord tones often enough to sound deliberate.
 *
 * Compare with `tracks/ambient_colony_day.ts`, which is the old style: 60 lines
 * of hand-written note names that cannot be varied without a musician. This is
 * twelve lines that can be randomised forever.
 *
 * Every layer is rendered separately so the game can fade them, and each layer
 * declares the intensity at which it enters. That single number is what turns a
 * piece into an adaptive score.
 */

import { type Rng, makeRng } from "./rng.js";
import {
  type Degree, type Key, bassNote, chordNotes, chordToneBias, parseKey, parseProgression,
} from "./theory.js";
import type { Hit, Layers } from "./approaches.js";
import { emptyLayers } from "./approaches.js";
import { playInstrument } from "./engine.js";
import { type Groove, GROOVES, grooveHits } from "./grooves.js";

export type LayerRole = "pad" | "bass" | "rhythm" | "melody" | "perc";

export interface LayerSpec {
  instrument: string;
  /** 0..1, roughly how much of the available space this layer fills. */
  density?: number;
  /** Lowest octave this layer may use. */
  octave?: number;
  volume?: number;
  /**
   * Intensity at which this layer becomes audible in game, 0..1.
   *
   * A pad at 0 is always there; percussion at 0.7 only arrives when something
   * is wrong. This is the contract between the music and the colony.
   */
  enterAt?: number;
  /**
   * How many distinct figures this layer invents before repeating them.
   *
   * One is an ostinato. Two or three is a phrase with an answer. Set it as high
   * as the number of chords and the layer never repeats itself, which sounds
   * exactly like the wash this generator used to produce.
   */
  motifs?: number;
  /**
   * 0..1, how much a motif is allowed to change on each repeat.
   *
   * At 0 the figure is literal every time, which turns mechanical over a long
   * loop. At 1 it is re-chosen constantly and stops being a figure at all. The
   * useful range is small: a fifth of the notes moving is plenty.
   */
  variation?: number;
  /**
   * Percussion only: which groove to play, from `grooves.ts`.
   *
   * Written out rather than generated, and that is the point. Every other layer
   * is invented because a wrong note inside the key is still music. A drum
   * pattern chosen at random is not a groove.
   */
  groove?: string;
  /**
   * A groove written out in full, instead of naming one.
   *
   * Takes precedence over `groove`. This is how a hand-edited beat travels
   * with the piece: the editor copies the named groove, changes the steps
   * someone clicked, and puts the result here. Nothing global is mutated, so
   * two pieces can carry two different edits of the same starting groove.
   */
  pattern?: Groove;
  /**
   * How this layer's notes are laid out in time.
   *
   * `motif` invents phrases and repeats them, which is what most music does.
   * `arpeggio` runs steadily through the chord instead, and `pulse` repeats one
   * note. Both are mechanical on purpose: a part that never varies is what
   * makes a piece sound driven rather than played, and it is most of the
   * difference between a colony at rest and a factory floor.
   */
  figure?: FigureStyle;
}

export type FigureStyle = "motif" | "arpeggio" | "pulse";

export interface Piece {
  name: string;
  key: string;
  tempo: number;
  /** Roman numerals, e.g. "i - VI - III - VII". */
  progression: string;
  /**
   * Requested length in seconds.
   *
   * When `loop` is set this is rounded to a whole number of progression cycles,
   * and one cycle can be long: four chords of four bars at 82bpm is 47 seconds,
   * so 72 becomes 94, not 72. Read the rendered length off the manifest rather
   * than assuming this value is what came out.
   */
  duration: number;
  barsPerChord?: number;
  seed?: number;
  fadeIn?: number;
  fadeOut?: number;
  /**
   * Render for seamless looping: no fades, and the length snapped to a whole
   * number of progression cycles.
   *
   * Both are required and neither is optional. A fade-in on a looping layer
   * dips every time round, and a length that is not a whole number of bars puts
   * the loop point mid-bar, which clicks. Anything the game plays continuously
   * wants this; a one-off stinger does not.
   */
  loop?: boolean;
  /**
   * How much room the piece is heard in, 0 to 1.
   *
   * Applied to each layer separately rather than to the mix, because the game
   * fades layers independently and reverb on the mix would take the melody's
   * tail with it the moment the melody dropped out.
   *
   * Defaults to a small amount rather than none: everything this engine makes
   * is dry, and dry is most of why generated music sounds generated.
   */
  space?: number;
  layers: Partial<Record<LayerRole, LayerSpec>>;
}

/**
 * The length actually rendered, and the bar grid it sits on.
 *
 * Looping pieces are snapped up to a whole number of progression cycles rather
 * than bars: stopping mid-progression would loop back to the tonic from the
 * wrong chord, which is in time and still wrong.
 */
export function resolveTiming(piece: Piece, chordCount: number) {
  const beat = 60 / piece.tempo;
  const bar = beat * 4;
  const barsPerChord = piece.barsPerChord ?? 4;
  const chordDur = bar * barsPerChord;
  const cycle = chordDur * chordCount;

  if (!piece.loop) return { beat, bar, chordDur, cycle, duration: piece.duration };

  const cycles = Math.max(1, Math.round(piece.duration / cycle));
  return { beat, bar, chordDur, cycle, duration: cycles * cycle };
}

export interface PieceDescription {
  /** Seconds for one trip through the progression. */
  cycleSeconds: number;
  /** What will actually render, after any snap to whole cycles. */
  duration: number;
  cycles: number;
  chordCount: number;
  /**
   * True when the melody layer is asked for but will never sound.
   *
   * The melody enters on the second pass through the progression, so a piece
   * under two cycles long has a melody stem of pure silence. That reads as a
   * bug in the renderer rather than a consequence of the length, so anything
   * showing a piece to a person should say so.
   */
  melodySilent: boolean;
}

/**
 * Check a piece and say what it will become, without rendering it.
 *
 * Throws on anything the renderer would throw on later. That is the point:
 * validating half the input means the other half fails several seconds into a
 * render, by which time the person has stopped connecting it to what they
 * typed.
 */
export function describePiece(piece: Piece): PieceDescription {
  parseKey(piece.key);
  const chords = parseProgression(piece.progression);
  if (chords.length === 0) throw new Error("progression is empty");
  if (!(piece.tempo > 0)) throw new Error(`tempo must be above zero, got ${piece.tempo}`);
  if (!(piece.duration > 0)) throw new Error(`length must be above zero, got ${piece.duration}`);

  const { cycle, duration } = resolveTiming(piece, chords.length);
  const cycles = duration / cycle;
  return {
    cycleSeconds: cycle,
    duration,
    cycles,
    chordCount: chords.length,
    melodySilent: cycles < 2 && Boolean(piece.layers.melody),
  };
}

/**
 * What a layer gets when the piece does not say.
 *
 * Exported so the editor can show these as its starting values rather than
 * carrying a second copy that quietly disagrees.
 */
/**
 * `pattern` is excluded because there is no sensible default for it: a groove
 * written out in full is either present or it is not, and forcing a value here
 * would mean inventing an empty one that silently plays nothing.
 */
type LayerDefaults = Required<Omit<LayerSpec, "instrument" | "pattern">>;

export const DEFAULTS: Record<LayerRole, LayerDefaults> = {
  pad: { density: 1.0, octave: 3, volume: 0.06, enterAt: 0.0, motifs: 1, variation: 0, groove: "none", figure: "motif" },
  bass: { density: 1.0, octave: 2, volume: 0.11, enterAt: 0.0, motifs: 1, variation: 0, groove: "none", figure: "motif" },
  rhythm: { density: 0.5, octave: 4, volume: 0.09, enterAt: 0.45, motifs: 2, variation: 0.15, groove: "none", figure: "motif" },
  melody: { density: 0.4, octave: 4, volume: 0.11, enterAt: 0.7, motifs: 2, variation: 0.2, groove: "none", figure: "motif" },
  perc: { density: 1.0, octave: 3, volume: 0.16, enterAt: 0.35, motifs: 1, variation: 0, groove: "backbeat", figure: "motif" },
};

/** A layer with every defaultable field filled in. `pattern` may still be absent. */
type ResolvedLayer = LayerDefaults & { instrument: string; pattern?: Groove };

function spec(role: LayerRole, given: LayerSpec): ResolvedLayer {
  return { ...DEFAULTS[role], ...given };
}

/**
 * One slot of a motif: when to play, how long for, and which note to reach for.
 *
 * The note is stored as a *position in the pool* rather than a note name, so
 * the same motif played over a different chord picks that chord's equivalent
 * note. Rhythm stays put, pitch follows the harmony, which is how a figure
 * develops instead of merely repeating.
 */
interface MotifSlot {
  offset: number;
  length: number;
  /** 0..1, scaled into whichever pool `chordTone` selects. */
  pick: number;
  chordTone: boolean;
}

type Motif = MotifSlot[];

/**
 * Invent one motif.
 *
 * Offsets land on half-beat positions rather than anywhere, because a figure
 * that can start at 3.37 beats does not sound loose, it sounds broken. Density
 * decides how many of the available slots get used.
 */
function makeMotif(
  rng: Rng,
  beatsPerChord: number,
  density: number,
  chordToneChance: number,
): Motif {
  const slots: number[] = [];
  for (let b = 0; b < beatsPerChord; b += 0.5) slots.push(b);

  const wanted = Math.max(1, Math.round(slots.length * density * 0.35));
  const chosen: number[] = [];
  const available = [...slots];
  for (let i = 0; i < wanted && available.length; i++) {
    chosen.push(available.splice(rng.int(available.length), 1)[0]);
  }
  chosen.sort((a, b) => a - b);

  return chosen.map((offset, i) => {
    const nextOffset = chosen[i + 1] ?? beatsPerChord;
    // Never overlap the next hit. Overlapping notes in a plucked voice turn a
    // figure into a smear, and the fix is scheduling rather than envelopes.
    const maxLen = Math.max(0.5, nextOffset - offset);
    return {
      offset,
      length: Math.min(maxLen, rng.pick([0.5, 1, 1.5, 2, 3])),
      pick: rng.next(),
      chordTone: rng.next() < chordToneChance,
    };
  });
}

/**
 * Play a motif against a chord, optionally nudging it.
 *
 * `variation` is the chance that any given note is re-chosen on this pass. At
 * zero the figure is literal every time, which gets mechanical; at one it is a
 * fresh figure every chord, which is where this generator started and why every
 * seed sounded like the same wash. Somewhere in between is a phrase that is
 * recognisably itself and not identical.
 */
function playMotif(
  motif: Motif,
  rng: Rng,
  chordTones: string[],
  others: string[],
  variation: number,
): Hit[] {
  return motif.map((slot) => {
    const pick = variation > 0 && rng.next() < variation ? rng.next() : slot.pick;
    const pool = slot.chordTone && chordTones.length ? chordTones : others.length ? others : chordTones;
    const note = pool[Math.min(pool.length - 1, Math.floor(pick * pool.length))];
    return [note, slot.offset, slot.length] as Hit;
  });
}

/** Ways an arpeggio can walk a chord. */
const ARP_SHAPES = ["up", "down", "updown", "downup"] as const;
type ArpShape = (typeof ARP_SHAPES)[number];

/**
 * A steady run through the chord, rather than a phrase.
 *
 * Everything about this is regular: same subdivision throughout, no rests, no
 * variation. That is the point. An arpeggio is the sound of a machine keeping
 * time, and making it "more musical" would remove the only thing it is for.
 *
 * Density picks the subdivision rather than how many slots get used, because a
 * sparse arpeggio is just a slow arpeggio.
 */
function arpeggioHits(
  beatsPerChord: number,
  density: number,
  pool: string[],
  shape: ArpShape,
  hold: number,
): Hit[] {
  if (!pool.length) return [];
  const step = density > 0.66 ? 0.25 : density > 0.33 ? 0.5 : 1;

  // The order the pool is walked in. Built once so the run continues across
  // the whole chord rather than restarting on every note.
  let order: string[] = pool;
  if (shape === "down") order = [...pool].reverse();
  if (shape === "updown") order = [...pool, ...[...pool].reverse().slice(1, -1)];
  if (shape === "downup") {
    const down = [...pool].reverse();
    order = [...down, ...[...down].reverse().slice(1, -1)];
  }

  const hits: Hit[] = [];
  for (let i = 0, at = 0; at < beatsPerChord; i++, at += step) {
    hits.push([order[i % order.length], at, step * hold]);
  }
  return hits;
}

/** One note, repeated. The simplest thing that still counts as a part. */
function pulseHits(beatsPerChord: number, density: number, pool: string[], hold: number): Hit[] {
  if (!pool.length) return [];
  const step = density > 0.66 ? 0.5 : density > 0.33 ? 1 : 2;
  const hits: Hit[] = [];
  for (let at = 0; at < beatsPerChord; at += step) hits.push([pool[0], at, step * hold]);
  return hits;
}

/**
 * Which motif each chord of the progression uses.
 *
 * Shapes rather than a random choice per chord, because a phrase heard in a
 * pattern is a phrase and one heard at random is noise with a small vocabulary.
 * ABAC is the shape most tunes are built on and it is first for that reason.
 */
const MOTIF_SHAPES = [
  [0, 1, 0, 2],
  [0, 1, 0, 1],
  [0, 0, 1, 1],
  [0, 1, 2, 1],
  [0, 1, 1, 2],
];

function motifOrder(rng: Rng, chordCount: number, motifCount: number): number[] {
  if (motifCount <= 1) return new Array(chordCount).fill(0);
  const shape = rng.pick(MOTIF_SHAPES);
  return Array.from({ length: chordCount }, (_, i) => shape[i % shape.length] % motifCount);
}

export interface CompiledPiece {
  layers: Layers;
  /** What the game needs to mix this: bar length, layer entry points, seed. */
  manifest: {
    name: string;
    key: string;
    tempo: number;
    seed: number;
    barSeconds: number;
    duration: number;
    /** True when this renders seamlessly and the game should loop it. */
    loop: boolean;
    /** Length of one trip through the progression. Sections switch on these. */
    cycleSeconds: number;
    layers: { role: LayerRole; enterAt: number; instrument: string }[];
  };
}

/**
 * A random stream of its own for each layer.
 *
 * One shared stream running through the layers in order meant that nudging the
 * melody's density silently rewrote the drums, because every later draw shifted
 * along with it. That is confusing on its own terms: a person changing one
 * control expects one thing to change.
 *
 * It also makes each layer independently reproducible, which is what lets the
 * editor re-render only the layer someone touched instead of the whole piece.
 *
 * Mixed rather than added, so `seed + 1` and the next role do not collide.
 */
function layerRng(seed: number, role: LayerRole) {
  const salt = ["pad", "bass", "rhythm", "melody", "perc"].indexOf(role) + 1;
  return makeRng(Math.imul(seed ^ 0x9e3779b9, 0x85ebca6b) + Math.imul(salt, 0xc2b2ae35));
}

/**
 * Compile a piece to layer buffers.
 *
 * `only` limits which layers are actually synthesised; the rest come back as
 * silence. That is worth having because synthesis is nearly all of the cost, so
 * re-rendering one layer someone just tweaked is a fifth of the work.
 *
 * It is only sound because each layer draws from its own random stream
 * (`layerRng`). With one shared stream, compiling a subset would produce
 * different notes from compiling everything, and the editor would quietly drift
 * away from what an export contains.
 */
export function compile(piece: Piece, only?: ReadonlySet<LayerRole>): CompiledPiece {
  const wanted = (role: LayerRole) => !only || only.has(role);
  return compileInner(piece, wanted);
}

function compileInner(piece: Piece, wanted: (role: LayerRole) => boolean): CompiledPiece {
  const key: Key = parseKey(piece.key);
  const progression: Degree[] = parseProgression(piece.progression);
  const seed = piece.seed ?? 1;
  const rhythmRng = layerRng(seed, "rhythm");
  const melodyRng = layerRng(seed, "melody");

  const barsPerChord = piece.barsPerChord ?? 4;
  const beatsPerChord = barsPerChord * 4;
  const { beat, bar, chordDur, cycle, duration: dur } =
    resolveTiming(piece, progression.length);

  const layers = emptyLayers(dur);

  // Motifs are invented once for the whole piece and then played against each
  // chord. That single change is most of the difference between a track that
  // sounds composed and one that sounds like an ambient wash: a phrase the ear
  // has heard before is a phrase, and a fresh random figure every chord is
  // texture no matter how good each individual figure is.
  const rhythmSpec = piece.layers.rhythm ? spec("rhythm", piece.layers.rhythm) : null;
  const melodySpec = piece.layers.melody ? spec("melody", piece.layers.melody) : null;

  const rhythmMotifs = rhythmSpec && rhythmSpec.figure === "motif"
    ? Array.from({ length: Math.max(1, rhythmSpec.motifs) }, () =>
        makeMotif(rhythmRng, beatsPerChord, rhythmSpec.density, 0.8))
    : [];
  const melodyMotifs = melodySpec && melodySpec.figure === "motif"
    ? Array.from({ length: Math.max(1, melodySpec.motifs) }, () =>
        makeMotif(melodyRng, beatsPerChord, melodySpec.density, 0.6))
    : [];

  const rhythmArp = rhythmSpec?.figure === "arpeggio" ? rhythmRng.pick(ARP_SHAPES) : "up";
  const melodyArp = melodySpec?.figure === "arpeggio" ? melodyRng.pick(ARP_SHAPES) : "up";

  /** Lay out one layer's notes for a chord, whichever style it uses. */
  const layerHits = (
    s: ResolvedLayer, motifs: Motif[], order: number[], ci: number,
    chordTones: string[], others: string[], arp: ArpShape, rng: Rng,
  ): Hit[] => {
    if (s.figure === "arpeggio") {
      // 0.9 rather than 1 so consecutive notes do not run into each other,
      // which turns a run into a smear on anything with a slow release.
      return arpeggioHits(beatsPerChord, s.density, chordTones, arp, 0.9);
    }
    if (s.figure === "pulse") return pulseHits(beatsPerChord, s.density, chordTones, 0.9);
    return playMotif(motifs[order[ci]], rng, chordTones, others, s.variation);
  };

  const rhythmOrder = motifOrder(rhythmRng, progression.length, rhythmMotifs.length);
  const melodyOrder = motifOrder(melodyRng, progression.length, melodyMotifs.length);

  const percSpec = piece.layers.perc ? spec("perc", piece.layers.perc) : null;
  const percGroove = percSpec
    ? piece.layers.perc?.pattern ?? GROOVES[percSpec.groove] ?? null
    : null;
  if (percSpec && percSpec.groove && !percGroove) {
    // Naming a groove that does not exist is a typo, and silently dropping the
    // drums would leave a piece that renders fine and is missing its beat.
    throw new Error(`unknown groove: ${percSpec.groove}`);
  }
  const percHits = percGroove ? grooveHits(percGroove) : [];
  const percFill = percGroove ? grooveHits(percGroove, true) : [];

  // A looping piece fills its whole length exactly; a one-shot leaves the last
  // two seconds clear so the fade-out has something to fade.
  const fillUntil = piece.loop ? dur : dur - 2;

  let t = 0;
  let pass = 0;
  while (t + chordDur <= fillUntil) {
    for (let ci = 0; ci < progression.length; ci++) {
      if (t + chordDur > fillUntil) break;
      const degree = progression[ci];

      if (piece.layers.pad && wanted("pad")) {
        const s = spec("pad", piece.layers.pad);
        playInstrument(layers.pad, s.instrument, chordNotes(key, degree, s.octave), t, chordDur * 0.9, s.volume);
      }

      if (piece.layers.bass && wanted("bass")) {
        const s = spec("bass", piece.layers.bass);
        playInstrument(layers.bass, s.instrument, bassNote(key, degree, s.octave), t, chordDur * 0.8, s.volume);
      }

      // Percussion runs on the bar rather than the chord, because that is what
      // a drummer does: the groove carries on through a chord change and is
      // most of what makes a change feel like it landed somewhere.
      if (percSpec && percGroove && wanted("perc")) {
        // The fill lands on the very last bar before the progression comes
        // round again. That is the one place a listener is already expecting
        // something, so it reads as the part arriving rather than a hiccup.
        const lastChord = ci === progression.length - 1;
        for (let barIndex = 0; barIndex < barsPerChord; barIndex++) {
          const barStart = t + barIndex * bar;
          const isFillBar = lastChord && barIndex === barsPerChord - 1;
          for (const hit of isFillBar ? percFill : percHits) {
            const at = barStart + hit.offsetBeats * beat;
            if (at >= dur) continue;
            playInstrument(
              layers.perc, hit.drum, hit.note, at,
              // Drums are shaped entirely by their envelope, so the length
              // passed in only has to be long enough not to truncate one.
              0.5, percSpec.volume * hit.gain,
            );
          }
        }
      }

      if (rhythmSpec && wanted("rhythm")) {
        const s = rhythmSpec;
        const { chordTones, others } = chordToneBias(key, degree, s.octave, s.octave + 1);
        // Rhythm figures sit mostly on chord tones. It is the difference
        // between an ostinato and a wrong note repeated confidently.
        const hits = layerHits(s, rhythmMotifs, rhythmOrder, ci, chordTones, others, rhythmArp, rhythmRng);
        for (const [note, off, len] of hits) {
          const at = t + off * beat;
          if (at < dur) playInstrument(layers.rhythm, s.instrument, note, at, len * beat, s.volume);
        }
      }

      // The melody waits for the second pass through the progression, so the
      // piece states itself before it decorates. Inherited from the original
      // and worth keeping: it is most of why those tracks have a shape.
      if (pass > 0 && melodySpec && wanted("melody")) {
        const s = melodySpec;
        const { chordTones, others } = chordToneBias(key, degree, s.octave + 1, s.octave + 2);
        const hits = layerHits(s, melodyMotifs, melodyOrder, ci, chordTones, others, melodyArp, melodyRng);
        for (const [note, off, len] of hits) {
          const at = t + off * beat;
          if (at + len * beat < dur) {
            playInstrument(layers.melody, s.instrument, note, at, len * beat, s.volume);
          }
        }
      }

      t += chordDur;
    }
    pass++;
  }

  const manifest: CompiledPiece["manifest"] = {
    name: piece.name,
    key: piece.key,
    tempo: piece.tempo,
    seed,
    barSeconds: bar,
    duration: dur,
    loop: piece.loop ?? false,
    cycleSeconds: cycle,
    layers: (Object.keys(piece.layers) as LayerRole[]).map((role) => {
      const s = spec(role, piece.layers[role]!);
      return { role, enterAt: s.enterAt, instrument: s.instrument };
    }),
  };

  return { layers, manifest };
}
